import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdvertiserOptionBean, UserPropsBean } from '../api/base/api'
import { businessDevelopmentStore } from '../util/BusinessDevelopmentStore'
import { getFont, setLoggedUserProps } from '../util/Util'
import UserProps from '../util/UserProps'
import { advAccountApi } from '../views/account/AdvAccountApi'
import FilterBarGlobalProps from '../views/filterbar/FilterBarGlobalProps'
import './userPropsGate.css'

function sortAdvertiserOptions(options: AdvertiserOptionBean[]): AdvertiserOptionBean[] {
  return options
    .slice()
    .sort((a, b) =>
      a.accountAdvertiserId === 0 ? 1 : b.accountAdvertiserId === 0 ? -1 : (a.name || '').localeCompare(b.name || ''),
    )
}

type Props = Pick<UserProps, 'setUser'> & FilterBarGlobalProps & { userPropsVersion: number }

export const UserPropsGate: React.FC<Props> = ({ setUser, filters, userPropsVersion, children }) => {
  const { t, i18n } = useTranslation('global')

  const [status, setStatus] = useState<'firstResolving' | 'resolving' | 'ready' | 'failed'>('firstResolving')
  const [error, setError] = useState<string>()

  const statusRef = useRef(status)
  statusRef.current = status
  const requestedScopeKeyRef = useRef<string | undefined>(undefined)

  const fail = (err: any, fallback: string) => {
    setError((err && err.error) || fallback)
    setStatus('failed')
  }

  useEffect(() => {
    businessDevelopmentStore.reset()
    filters.advertiserOptions = undefined

    setLoggedUserProps(setUser)
      .then((userProps: UserPropsBean) => {
        if (!userProps || !userProps.showAdvertiserSelector) {
          setStatus('ready')
          return
        }

        return advAccountApi
          .getUserAdvertisers()
          .then((options: AdvertiserOptionBean[]) => {
            if (!options.length) {
              fail(undefined, 'This account has no advertisers to report on')
              return
            }

            const sortedOptions = sortAdvertiserOptions(options)

            filters.advertiserOptions = sortedOptions

            requestedScopeKeyRef.current = `${sortedOptions[0].accountAdvertiserId}`
            businessDevelopmentStore.setAccountAdvertiserId(sortedOptions[0].accountAdvertiserId as number)

            return setLoggedUserProps(setUser).then(() => setStatus('ready'))
          })
          .catch((err) => fail(err, 'Failed to load user data, reload the page'))
      })
      .catch((err) => fail(err, 'Failed to load the dashboard, reload the page'))
  }, [])

  useEffect(() => {
    return businessDevelopmentStore.subscribe(() => {
      const scopeKey = businessDevelopmentStore.scopeKey

      if (statusRef.current !== 'ready' || scopeKey === undefined || scopeKey === requestedScopeKeyRef.current) {
        return
      }

      requestedScopeKeyRef.current = scopeKey
      setStatus('resolving')
      setLoggedUserProps(setUser)
        .then(() => setStatus('ready'))
        .catch((err) => fail(err, 'Failed to load the dashboard, reload the page'))
    })
  }, [])

  return (
    <>
      {(status === 'resolving' || status === 'ready') && (
        <React.Fragment key={userPropsVersion}>{children}</React.Fragment>
      )}
      {status !== 'ready' && (
        <div className="user-props-gate" style={getFont(i18n)}>
          <div className="gate-red-background" />
          <div className="gate-blue-background" />
          <div className="gate-group">
            {(status === 'firstResolving' || status === 'resolving') && (
              <>
                <div className="gate-title">{t('Please wait')}</div>
                <div className="gate-text">{t('Loading advertiser info')}...</div>
                <div className="gate-spinner" />
              </>
            )}
            {status === 'failed' && <div className="gate-error">{error}</div>}
          </div>
        </div>
      )}
    </>
  )
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/layouts/UserPropsGate.tsx